import { Box, Dialog, DialogActions, DialogContent, Divider, Typography } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FilesTable } from "@/components/Tables/Aeros/components/FilesTable"
import { useFileColumns } from "@/components/Tables/Aeros/hooks/useFileColumns"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import type { VendorApLedgerEntry } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"

interface AccountsPayableTransactionDocumentsDialogProps {
  entry: VendorApLedgerEntry
  onClose: () => void
}

export const AccountsPayableTransactionDocumentsDialog: FC<AccountsPayableTransactionDocumentsDialogProps> = ({
  entry,
  onClose,
}) => {
  const invoiceId = entry.invoice?.invoiceId

  const { fileList, fileFetching } = useGetFileUploadData({
    referenceId: invoiceId,
    referenceTableName: ReferenceTableName.Invoice,
    pause: !invoiceId,
  })

  const documents = useMemo(() => fileList.filter((signedUrl) => !signedUrl.file?.isArchived), [fileList])

  const columns = useFileColumns({ canDownload: true })

  const referenceNumber = entry.invoice?.referenceNumber

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth data-testid='accounts-payable.documents-dialog'>
      <DialogHeader title='Documents' onClose={onClose} />
      <DialogContent>
        <Box className='flex flex-col gap-6 px-5 py-3'>
          <Box className='flex flex-col gap-3'>
            <Typography className='text-base font-bold text-gray-700'>{entry.invoice?.invoiceNumber}</Typography>
            {referenceNumber && (
              <Box className='flex items-start gap-10'>
                <Box className='flex flex-col gap-1'>
                  <Typography className='text-sm font-bold text-gray-700'>Reference Number</Typography>
                  <Typography variant='body2' className='text-gray-700'>
                    {referenceNumber}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box className='flex flex-col gap-2.5'>
            <Typography className='text-base font-medium text-gray-700'>View Documents</Typography>
            <Divider />
          </Box>

          <FilesTable
            data={documents}
            isLoading={fileFetching}
            columns={columns}
            noDataMessage='No documents found for this invoice'
          />
        </Box>
      </DialogContent>

      <DialogActions className='flex items-center justify-end px-5 pb-5'>
        <Button appearance='contained' onClick={onClose} data-testid='accounts-payable.documents-dialog.close-button'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
